import { createAction } from '@reduxjs/toolkit';
import type { AnyApi } from './buildCreateApi';
import type { EndpointDefinitions, CombinedState } from './rtk-query-types';
import type { EndpointQueryFlow } from './apiSSRModule/buildEndpointQueryFlow';
import type { AnyEndpointQuery, AnyQueryDefinition } from './apiSSRModule/types';

export const globalInitialStateVariable = '__REDUX_INITIAL_STATE__';

/**
 * Максимальное время хранения подписки на гидрированные данные, в секундах.
 * Подписка гидрации удаляется сразу при появлении настоящей подписки (монтирование компонента-потребителя),
 * а этот таймаут — лишь страховка на случай, если компонент так и не смонтируется.
 */
const HYDRATED_DATA_SUBSCRIPTION_TIMEOUT_SECONDS = 60;

declare global {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DIRECT-239681
    var __REDUX_INITIAL_STATE__: Record<string, any> | undefined;
}

/**
 * Метод, подготавливающий данные редьюсера, полученные при SSR, для гидрации на клиенте.
 *
 * @param moduleName – Название модуля.
 * @param reducerPath – Путь по которому редьюсер подключается в стор.
 * @returns Подготовленные данные редьюсера для гидрации стора на клиенте.
 * @internal
 */
export function extractReducerRehydrationInfo(moduleName: string, reducerPath: string) {
    return global[globalInitialStateVariable]?.[moduleName]?.[reducerPath];
}

/**
 * Для каждого гидрированного запроса создаем поток и держатель-подписку на него, чтобы RTK Query
 * не удалил данные из кеша между моментом гидрации и моментом первого рендера компонента,
 * который их использует. Держатель снимается сразу при появлении настоящей подписки на поток
 * (монтирование компонента-потребителя), а если компонент так и не смонтировался — по таймауту.
 *
 * @param api – Инстанс RTK Query API
 * @param state – Данные для гидрации
 * @internal
 */
export function subscribeToHydratedApiData<
    Definitions extends EndpointDefinitions,
    ReducerPath extends string,
    TagTypes extends string,
>(api: AnyApi, state: CombinedState<NoInfer<Definitions>, NoInfer<TagTypes>, NoInfer<ReducerPath>>) {
    for (const [, queryStatus] of Object.entries(state.queries)) {
        const endpointName = queryStatus?.endpointName;
        if (!endpointName) {
            continue;
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- эндпоинт расширяется модулем apiSSRModule методом queryFlow
        const endpoint = api.endpoints[endpointName] as
            | (AnyEndpointQuery & EndpointQueryFlow<AnyQueryDefinition>)
            | undefined;
        if (!endpoint?.queryFlow) {
            continue;
        }

        const flow = endpoint.queryFlow(queryStatus.originalArgs, { prefetch: false });
        flow.prefetch({ keepPrefetchedDataFor: HYDRATED_DATA_SUBSCRIPTION_TIMEOUT_SECONDS }).catch(console.error);
    }
}

/**
 * Создает экшен для гидрации данных в RTK Query API.
 *
 * @param moduleName – Название модуля.
 * @param reducerPath – reducerPath, переданный в `createApi()`.
 * @returns Экшен для гидрации данных в RTK Query API.
 * @internal
 */
export function createHydrateApiAction<ApiState>(moduleName: string, reducerPath: string) {
    return createAction<ApiState>(`${moduleName}/${reducerPath}/hydrate`);
}

/**
 * Создает экшен для гидрации данных в слайсе.
 *
 * @param sliceName – name, переданный в `createSlice()`.
 * @returns Экшен для гидрации данных в слайсе.
 * @internal
 */
export function createHydrateSliceAction(sliceName: string) {
    return createAction(`${sliceName}/hydrate`);
}
